import { AspectRatio, Image } from '@chakra-ui/react';
import { Auth } from '~/components/auth/page';
import {
  UserAction,
  NestedHome,
  NestedProject,
  TODO,
  isSellerEvent,
} from '@habity/types';
import { ClaimedProperty } from '~/components/ui/svg';
import { useTranslations } from 'next-intl';
import { createClient } from '~/lib/providers/supabase/client';
import { useAgency } from '~/hooks/useAgency';
import { useSearchParams, usePathname } from 'next/navigation';
import { ROUTES } from '@habity/constants';
import { getStreet, getImageUrl } from '@habity/services';

export const LoginForm = ({
  authHome,
  authProject,
  authAction,
  redirectTo,
}: {
  authHome: NestedHome | null;
  authProject: NestedProject | null;
  authAction: UserAction | null;
  redirectTo?: string;
}) => {
  const supabase = createClient();
  const { agency, theme, getLogoSrc } = useAgency();
  const { auth_providers } = agency || {};

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const redirectedFrom = searchParams?.get('redirectedFrom');
  const tTitle = useTranslations('Login.Title');
  const tDescription = useTranslations('Login.Description');

  const getLoginLogo = (home?: NestedHome, project?: NestedProject) => {
    if (home) {
      const { images } = home || {};
      return images && images.length > 0 ? (
        <AspectRatio ratio={1} w="40%" mt="4">
          <Image
            borderRadius="full"
            border="1px solid #000"
            src={getImageUrl(images[0], 'list')}
          />
        </AspectRatio>
      ) : (
        <ClaimedProperty w="100%" mt="4" />
      );
    }
    return (
      <Image src={getLogoSrc()} maxW={theme?.logo_max_width} pt={3} pb={1} />
    );
  };

  const getStreetParam = (
    home?: NestedHome,
    project?: NestedProject,
    isSellerEventAction?: boolean
  ) => {
    if (home) return getStreet(home);
    if (project)
      return project.name || project.heading || project.city || 'detta projekt';
    return isSellerEventAction
      ? tTitle('Fallback.yourHome')
      : tTitle('Fallback.dreamHomes');
  };

  const getLoginTitle = (
    home?: NestedHome,
    project?: NestedProject,
    action?: UserAction
  ) => {
    if (!action) return undefined;
    const isSellerEventAction = isSellerEvent(action);
    return tTitle(action, {
      street: getStreetParam(home, project, isSellerEventAction),
    });
  };

  const getLoginDescription = (
    home?: NestedHome,
    project?: NestedProject,
    action?: UserAction
  ) => {
    if (!action) return undefined;
    if (!tDescription.has(action)) return undefined;
    const isSellerEventAction = isSellerEvent(action);
    return tDescription.rich(action, {
      strong: (chunks) => <strong>{chunks}</strong>,
      street: getStreetParam(home, project, isSellerEventAction),
    });
  };

  if (!agency) return null;

  const finalRedirectTo = `${
    redirectTo || redirectedFrom || pathname || ROUTES.ROOT
  }`;

  return (
    <Auth
      redirectTo={finalRedirectTo}
      supabaseClient={supabase as TODO}
      view="magic_link"
      providers={authHome ? [] : auth_providers || []}
      linkUrl={`mailto:${agency?.support_email}`}
      logo={getLoginLogo(authHome || undefined, authProject || undefined)}
      title={getLoginTitle(
        authHome || undefined,
        authProject || undefined,
        authAction || undefined
      )}
      description={getLoginDescription(
        authHome || undefined,
        authProject || undefined,
        authAction || undefined
      )}
      agencyId={agency.id}
    />
  );
};
